import Keycloak from 'keycloak-js';
import { AdminAuth } from '@nsftx/seven-client-sdk-admin';
import IAM from '@/services/api/IAM';
import { TokenStorage } from '@/services/TokenStorage';
import { postLoginRedirect } from '@/services/postLoginRedirect';
import store from '@/store';

// The AdminAuth SDK only accepts these two environments and derives the Guards
// SSO host from them internally. We mirror that mapping here because the silent
// SSO check below talks to Keycloak directly and therefore needs the host.
const GUARDS_SSO_HOSTS = {
  staging: 'https://svn-sso.staging.de-3.nsoft.cloud',
  production: 'https://sxg-god-auth-kyc-sxg.de-2.nsoft.cloud',
};

// A missing VUE_APP_GUARDS_ENVIRONMENT must not silently point a production
// build at staging SSO (or the reverse), so fall back to the build mode.
const DEFAULT_GUARDS_ENVIRONMENT = process.env.NODE_ENV === 'production' ? 'production' : 'staging';

const GUARDS_ENVIRONMENT = Object.prototype.hasOwnProperty.call(GUARDS_SSO_HOSTS, process.env.VUE_APP_GUARDS_ENVIRONMENT || '')
  ? process.env.VUE_APP_GUARDS_ENVIRONMENT
  : DEFAULT_GUARDS_ENVIRONMENT;

const GUARDS_REALM = process.env.VUE_APP_GUARDS_REALM || 'guards';
const GUARDS_CLIENT_ID = process.env.VUE_APP_GUARDS_CLIENT_ID || 'admin-dash';
const GUARDS_SSO_URL = process.env.VUE_APP_GUARDS_SSO_URL || `${GUARDS_SSO_HOSTS[GUARDS_ENVIRONMENT]}/auth`;

function jwtDecode(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
    const decoded = JSON.parse(jsonPayload);
    // Check if token has expired
    if (decoded.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      if (decoded.exp < currentTime) {
        console.warn("Token has expired!");
        return null;
      }
    }
    return decoded;
  } catch (error) {
    console.error("Invalid JWT token", error);
    return null;
  }
}

const ADMIN_TOKEN_KEY = 'admin_token';

// Standalone exchange function — can be properly awaited from the bootstrap in main.js
async function exchangeGuardsToken(token) {
  if (!token) return;
  localStorage.setItem(ADMIN_TOKEN_KEY, token);

  // Prefer the email straight off the Guards token when it carries one.
  const guardsClaims = jwtDecode(token);
  const guardsEmail = guardsClaims?.email || guardsClaims?.preferred_username || null;
  if (guardsEmail) store.commit('SET_USER_EMAIL', guardsEmail);

  try {
    // Exchange the Guards token for a Device Management token.
    const { data } = await IAM.loginUsingGuards(token);
    const dmToken = data?.access_token || data?.token;
    if (!dmToken) {
      console.error('Guards response is missing the Device Management token');
      return;
    }

    // Fall back to the DM token when the Guards token had no email claim.
    if (!guardsEmail) {
      const dmClaims = jwtDecode(dmToken);
      if (dmClaims?.email) store.commit('SET_USER_EMAIL', dmClaims.email);
    }

    // The Guards token is already in localStorage under admin_token; this stores
    // the DM token under iam_data so the regular axios interceptors pick it up.
    TokenStorage.storeIAMData({
      access_token: dmToken,
      refresh_token: data?.refresh_token || null,
      expires_in: data?.expires_in || 3600,
    });

    // Clear SSO attempted flag on successful login
    sessionStorage.removeItem('sso_attempted');
    sessionStorage.removeItem('sso_attempts');

    // Use a hard navigation so the page re-initialises with the fresh DM token
    // already in localStorage. This is more reliable than router.replace() which
    // can race with the router guard when called before Vue has mounted, and it also
    // strips the Keycloak ?code=… callback params from the URL.
    const currentUrl = new URL(window.location.href);
    const callbackHash = currentUrl.hash.startsWith('#')
      ? currentUrl.hash.slice(1)
      : currentUrl.hash;
    const callbackHashParams = new URLSearchParams(callbackHash.startsWith('/') ? '' : callbackHash);
    const hasCallbackParams =
      (currentUrl.searchParams.has('code') && currentUrl.searchParams.has('session_state')) ||
      (callbackHashParams.has('code') && callbackHashParams.has('session_state'));
    const onLoginPage = window.location.pathname === '/login' ||
                        window.location.hash.startsWith('#/login');

    if (hasCallbackParams || onLoginPage) {
      // Send the user back to whatever deep link triggered the login flow,
      // forcing a full reload so App.vue remounts and re-runs init.
      postLoginRedirect.navigateAndReload();
    }
  } catch (e) {
    console.error('Guards exchange failed:', e);
  }
}

// Public paths that must never be redirected away from by SSO
const PUBLIC_PATHS = [
  '/account-registration',
  '/space-join',
  '/account-password-update',
  '/account-password-reset',
  '/forgot-password',
  '/change-password',
  '/update-account',
];

function isPublicPage() {
  return PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
}

const adminAuthInstance = new AdminAuth({
  environment: GUARDS_ENVIRONMENT,
  app: { displayId: 'deviceManagement' },
  debug: process.env.NODE_ENV !== 'production',
  events: {
    onTokenUpdated: async (token) => {
      // Never interfere with public pages
      if (isPublicPage()) return;

      if (!token) {
        console.warn('AdminAuth onTokenUpdated: token is undefined — no Guards session');
        sessionStorage.setItem('sso_failed', 'true');
        return;
      }
      // A valid token must always be processed regardless of sso_failed — the flag
      // only gates new initialization attempts (see initialize()), not token updates.
      await exchangeGuardsToken(token);
    }
  },
});

// Export instance so it can be passed to <scs-admin-menus>
export { adminAuthInstance };

// Silent SSO check using keycloak-js directly.
// Only checks for existing session via hidden iframe — never redirects.
// Returns true if user was silently authenticated, false otherwise.
async function silentSsoCheck() {
  const kc = new Keycloak({
    url: GUARDS_SSO_URL,
    realm: GUARDS_REALM,
    clientId: GUARDS_CLIENT_ID,
  });

  try {
    const authenticated = await kc.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
    });

    if (authenticated && kc.token) {
      await exchangeGuardsToken(kc.token);
      return true;
    }
  } catch (e) {
    console.warn('Silent SSO check failed:', e);
  }
  return false;
}

export const authService = {

  // Silent-only SSO check (no redirect). Used at bootstrap.
  silentSsoCheck,

  // Initialize AdminAuth - attempts silent SSO if no logout flag
  initialize() {
    // Check sessionStorage logout flag (set when user explicitly logs out)
    const logoutFlag = sessionStorage.getItem('logout');
    const ssoFailed = sessionStorage.getItem('sso_failed');

    if (logoutFlag) {
      // User explicitly logged out this session, don't attempt SSO
      return Promise.resolve();
    }

    // When the browser is landing on a Keycloak callback URL (?code=… or
    // #code=…) the user actively authenticated — we must always process that
    // callback even if a previous automatic SSO attempt failed (and set
    // sso_failed) earlier.
    const rawHash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(rawHash.startsWith('/') ? '' : rawHash);
    const isKeycloakCallback =
      !!(new URLSearchParams(window.location.search).get('code')) ||
      !!hashParams.get('code');

    if (ssoFailed && !isKeycloakCallback) {
      // SSO already failed this session and this is not a callback — don't retry
      return Promise.resolve();
    }

    return adminAuthInstance.initialize();
  },

  logout() {
    adminAuthInstance.logout();
    localStorage.removeItem('admin_token');
    localStorage.removeItem('isOwnLogin');
    TokenStorage.clear();
    // Explicit logout discards any captured deep-link target — the user is
    // intentionally leaving, not getting bounced.
    postLoginRedirect.clear();
    // Set logout flag in sessionStorage - will be cleared when browser reopens
    sessionStorage.setItem('logout', 'true');
    sessionStorage.removeItem('sso_failed');
  },

  getAdminToken() {
    return localStorage.getItem('admin_token');
  },
};
